// ─── TICKER CONFLUENCE ────────────────────────────────────────────────────────
// A persistent tape of fired convergence events — the moments where independent
// signals (dark pool, unusual options, congressional, earnings, FDA, legislation)
// agreed on one ticker at one time.
//
// ⚠⚠ READS /api/convergence/history, NOT /api/stream/convergence, AND THE
// DIFFERENCE IS THE WHOLE FEATURE. The stream endpoint serves a Redis view that
// dedupes by symbol, caps at 20, and can only see events still inside their 1h
// signal:convergence TTL — so it can show "what is firing now" and nothing else.
// The history endpoint reads the convergence_history table (migration 0014), so
// repeat fires stay distinct rows and the tape scrolls genuinely back through the
// session rather than through a rolling one-hour window.
//
// ⚠ THE TABLE FILLS FROM THE FIRST FIRE AFTER 0014 DEPLOYED — nothing backfills
// it. An empty tape is the correct reading of a quiet tape, not a broken fetch.
// The empty state says so rather than implying a failure.

import { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import { C, s, font, DataStatus, backendFetch } from "./core";
import { SearchBox, useRemoteSearch } from "./components/SearchBox";
import {
  BILL_BADGE_TITLE, BILL_NOTE, billBadge, billNumber, billStage, billUrl, scoreParts, signalWord,
} from "./billRules";

const HISTORY_PATH = "/api/convergence/history";
const REFRESH_MS = 30 * 1000;

// ⚠ Bands are on the detector's 0-130 composite, not 0-100 — FIRE_THRESHOLD is 40
// and the ceiling rose to 130 when legislation was added, so a "weak" reading here
// still cleared the bar to fire at all.
const scoreColor = (n) =>
  n >= 80 ? C.green : n >= 60 ? C.amber : n >= 40 ? C.text : C.textDim;

// Stored timestamps are UTC ISO; the desk reads ET.
const etClock = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", hour12: false, hour: "2-digit", minute: "2-digit",
  }).format(d);
};

// "14m ago" under the hour, "09:47 ET" beyond it.
// ⚠ Relative under the hour because that is the window where "is this still live"
// is the question; absolute beyond it because "6h ago" is harder to place against
// the session than a clock time.
const sinceLabel = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const age = Date.now() - t;
  if (age < 0) return etClock(iso);                    // clock skew — show the stamp
  if (age < 60 * 60 * 1000) return `${Math.floor(age / 60000)}m ago`;
  return `${etClock(iso)} ET`;
};

// ⚠ Only badges that actually fired. Rendering the full set greyed-out would make
// every row look identical at a glance, which defeats the point of a tape you scan.
// ⚠ earnings_proximity is a NUMBER OF DAYS and 0 is meaningful (reports today), so
// it is tested for null rather than truthiness — the trap that would silently drop
// the most urgent case.
function badgesFor(sig) {
  const g = sig || {};
  const out = [];
  if (g.dark_pool) out.push({ t: "DP", c: C.teal, full: "Dark pool" });
  if (g.unusual_options) out.push({ t: "UO", c: C.green, full: "Unusual options" });
  if (g.congressional) {
    out.push({ t: "CONG", c: C.red, full: "Reported congressional trades in this ticker. Select to see them.", kind: "congress" });
  }
  if (g.earnings_proximity != null) {
    out.push({ t: `EARN ${g.earnings_proximity}d`, c: C.amber, full: "Earnings proximity" });
  }
  if (g.fda_catalyst) out.push({ t: "FDA", c: C.purple, full: "FDA catalyst" });
  if (g.legislation) {
    out.push({ t: billBadge(g.legislation.policy_area), c: C.orange, full: BILL_BADGE_TITLE, kind: "bill" });
  }
  return out;
}

const badgeStyle = (b) => ({
  fontSize: 8, fontWeight: 700, color: b.c, border: `1px solid ${b.c}66`,
  borderRadius: 2, padding: "1px 5px", letterSpacing: "0.05em", whiteSpace: "nowrap",
});

const Badge = ({ b }) => <span title={b.full} style={badgeStyle(b)}>{b.t}</span>;

// ⚠ A button inside a clickable row: stopPropagation is what keeps it from also opening
// or closing the row.
const BadgeButton = ({ b, onPress, expanded, controls }) => (
  <button
    type="button"
    data-badge={b.kind}
    title={b.full}
    aria-expanded={expanded}
    aria-controls={controls}
    onClick={(e) => { e.stopPropagation(); onPress(); }}
    style={{ ...badgeStyle(b), background: "transparent", cursor: "pointer", fontFamily: font }}
  >{b.t}</button>
);

const legIsObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const present = (v) => typeof v === "string" && v.trim() !== "";

// ── The bill behind a sector-bill badge ───────────────────────────────────────
// ⚠⚠ ONLY WHAT THE SAVED ROW HOLDS. Rows are permanent and older ones carry less, so every
// field hides when absent rather than falling back to anything.
function BillPanel({ signals, id }) {
  const leg = legIsObject(signals?.legislation) ? signals.legislation : {};
  const number = billNumber(leg.bill_id);
  const url = billUrl(leg.bill_id);
  const stage = billStage(leg.tier);
  const parts = scoreParts(leg.score_parts);
  const fired = (Array.isArray(signals?.signals_fired) ? signals.signals_fired : [])
    .map(signalWord).filter(Boolean);
  const label = { color: C.textDim };
  return (
    <div data-bill-panel id={id} style={{
      margin: "2px 0 8px", padding: "8px 10px", background: C.panelAlt,
      border: `1px solid ${C.border}`, borderRadius: 3, fontFamily: font, fontSize: 11, color: C.textMid, lineHeight: 1.6,
    }}>
      {number && (
        <div data-bill-number style={{ fontWeight: 700, color: C.text }}>
          {url
            ? <a data-bill-link href={url} target="_blank" rel="noopener noreferrer" style={{ color: C.amber }}>{number}</a>
            : number}
        </div>
      )}
      {present(leg.title) && <div data-bill-title style={{ color: C.text }}>{leg.title}</div>}
      {present(leg.policy_area) && <div data-bill-area><span style={label}>Policy area: </span>{leg.policy_area}</div>}
      {stage && <div data-bill-stage><span style={label}>Stage: </span>{stage}</div>}
      {present(leg.sponsor_name) && <div data-bill-sponsor><span style={label}>Sponsor: </span>{leg.sponsor_name}</div>}
      {Number.isFinite(leg.legislation_score) && (
        <div data-bill-score><span style={label}>Bill score: </span>{leg.legislation_score}</div>
      )}
      {parts.length > 0 && (
        <div data-bill-parts>
          {parts.map((p) => <div key={p.key}><span style={label}>{p.word}: </span>{p.points}</div>)}
        </div>
      )}
      {fired.length > 0 && (
        <div data-bill-signals style={{ marginTop: 4 }}>
          <span style={label}>Signals fired: </span>{fired.join(" · ")}
        </div>
      )}
      <div data-bill-note style={{ marginTop: 6, color: C.textDim }}>{BILL_NOTE}</div>
    </div>
  );
}

const panelId = (symbol, key) => `bill-${symbol}-${key}`.replace(/[^A-Za-z0-9_-]/g, "_");

// ── One symbol: the anchor row, plus every fire when expanded ─────────────────
// ⚠ Memoised on what renders. The 30s poll rebuilds the whole list, and without
// this an expanded symbol with a dozen fires below it would re-render because some
// unrelated ticker fired — the same reason the dark-pool tape memoises its rows.
export const ConfluenceRow = memo(function ConfluenceRow({ group, open, onToggle, onCongressOpen }) {
  const anchor = group.anchor;
  const score = Math.round(Number(anchor.score) || 0);
  const col = scoreColor(score);
  const badges = badgesFor(anchor.signals);
  const count = group.fires.length;
  // Which fire's bill is open, by its timestamp. ⚠ Never by position: the 30s poll moves
  // fires down the list, and a position would put another fire's bill under an open panel.
  const [billOpen, setBillOpen] = useState(null);
  const anchorKey = `anchor-${anchor.triggered_at}`;
  const toggleBill = (key) => setBillOpen((cur) => (cur === key ? null : key));
  const renderBadge = (b, key) => (b.kind === "bill"
    ? <BadgeButton key={b.t} b={b} expanded={billOpen === key}
        controls={panelId(group.symbol, key)} onPress={() => toggleBill(key)} />
    : b.kind === "congress" && onCongressOpen
    ? <BadgeButton key={b.t} b={b} onPress={() => onCongressOpen(group.symbol)} />
    : <Badge key={b.t} b={b} />);

  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <div
        onClick={() => onToggle(group.symbol)}
        title={`${group.symbol} — click to ${open ? "collapse" : "expand"} all fires`}
        style={{
          display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
          cursor: "pointer", flexWrap: "wrap",
          background: open ? "rgba(255,255,255,0.04)" : "transparent",
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 700, color: C.amber, minWidth: 56 }}>
          {group.symbol}
        </span>
        <span style={{
          fontSize: 11, fontWeight: 700, color: col,
          border: `1px solid ${col}66`, borderRadius: 3, padding: "1px 6px",
        }}>{score}</span>
        {anchor.trigger_type && (
          <span style={{ fontSize: 8, color: C.textDim, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            {String(anchor.trigger_type).replace(/_/g, " ")}
          </span>
        )}
        <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {badges.map((b) => renderBadge(b, anchorKey))}
        </span>
        {/* ⚠ Only when a symbol has fired more than once — on a single fire the
            count is noise, and the chevron alone already signals expandability. */}
        {count > 1 && (
          <span style={{ fontSize: 9, color: C.textMid, whiteSpace: "nowrap" }}>
            {count} signals
          </span>
        )}
        <span style={{ marginLeft: "auto", fontSize: 9, color: C.textDim, whiteSpace: "nowrap" }}>
          {sinceLabel(anchor.triggered_at)}
        </span>
        <span style={{ fontSize: 9, color: C.textDim }}>{open ? "▲" : "▼"}</span>
      </div>

      {billOpen === anchorKey && (
        <div style={{ padding: "0 10px" }}>
          <BillPanel signals={anchor.signals} id={panelId(group.symbol, anchorKey)} />
        </div>
      )}

      {open && (
        <div style={{ padding: "2px 10px 10px 10px" }}>
          {group.fires.map((f, i) => {
            const fs = Math.round(Number(f.score) || 0);
            const fc = scoreColor(fs);
            const fb = badgesFor(f.signals);
            const fireKey = `fire-${f.triggered_at}`;
            return (
              <div key={`${f.triggered_at}-${i}`} style={{
                padding: "6px 0",
                borderTop: i === 0 ? "none" : `1px solid ${C.border}`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, color: C.textDim, minWidth: 52 }}>
                    {etClock(f.triggered_at)} ET
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: fc }}>{fs}</span>
                  <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {fb.map((b) => renderBadge(b, fireKey))}
                  </span>
                </div>
                {billOpen === fireKey && <BillPanel signals={f.signals} id={panelId(group.symbol, fireKey)} />}
                {f.narrative && (
                  <div style={{
                    fontSize: 10, color: C.textMid, fontStyle: "italic",
                    marginTop: 4, lineHeight: 1.5,
                  }}>{f.narrative}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

// ── Group by symbol, newest fire first ──
// ⚠ Grouping is CLIENT-side because the endpoint returns a flat list on purpose:
// flat keeps repeat fires distinct, and the server has no business picking a
// collapse rule. Groups are ordered by their most recent fire, so a symbol that
// just fired jumps to the top of the tape — the dark-pool tape's ordering.
function groupFires(rows) {
  const by = new Map();
  for (const r of rows) {
    if (!r || !r.symbol) continue;
    if (!by.has(r.symbol)) by.set(r.symbol, []);
    by.get(r.symbol).push(r);
  }
  const out = [];
  for (const [symbol, fires] of by) {
    // The endpoint already sorts triggered_at DESC, but sorting here as well
    // means the display does not silently depend on that contract holding.
    const sorted = [...fires].sort(
      (a, b) => String(b.triggered_at || "").localeCompare(String(a.triggered_at || ""))
    );
    out.push({ symbol, anchor: sorted[0], fires: sorted });
  }
  out.sort((a, b) => String(b.anchor.triggered_at || "")
    .localeCompare(String(a.anchor.triggered_at || "")));
  return out;
}

// ── Tab ───────────────────────────────────────────────────────────────────────
export function TickerConfluenceTab({ onCongressOpen }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("loading");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [expanded, setExpanded] = useState(null);   // one symbol at a time
  const [query, setQuery] = useState("");

  // ⚠ SERVER-SIDE, EXACT TICKER. The tape is the 200 newest fires of ~2,000 a
  // day, so a browser-side filter would only see the last hour or two. ?symbol=
  // is an exact match on an indexed column; a partial ticker is not a prefix
  // search, so the view says so instead of claiming "no matches" mid-word.
  const fetchSearch = useCallback(async (q) => {
    const path = `${HISTORY_PATH}?symbol=${encodeURIComponent(q)}`;
    const res = await backendFetch(path, path);
    if (!res?.ok) throw new Error(String(res?.status));
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  }, []);
  const search = useRemoteSearch(query, fetchSearch);
  const searchActive = useRef(false);
  searchActive.current = search.active;
  const searchRefresh = useRef(search.refresh);
  searchRefresh.current = search.refresh;

  const load = useCallback(async () => {
    try {
      const res = await backendFetch(HISTORY_PATH, HISTORY_PATH);
      if (!res?.ok) throw new Error(String(res?.status));
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
      setUpdatedAt(Date.now());
      setStatus("live");
    } catch {
      // ⚠ Rows are NOT cleared on a failed poll. The endpoint is cached 60s and
      // the tape polls at 30s, so a single blip would otherwise blank a populated
      // tape and read as "all signals gone" rather than "one request failed".
      setStatus("offline");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    // ⚠ The 30s tick refreshes the SEARCH while one is active, so it never
    // replaces the user's results with the full tape.
    const tick = () => {
      if (cancelled) return;
      if (searchActive.current) searchRefresh.current();
      else load();
    };
    tick();
    const id = setInterval(tick, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [load]);

  // Clearing the box brings the tape back fresh.
  const wasSearching = useRef(false);
  useEffect(() => {
    if (wasSearching.current && !search.active) load();
    wasSearching.current = search.active;
  }, [search.active, load]);

  // A new query never opens a row the user did not open.
  useEffect(() => { setExpanded(null); }, [search.q]);

  const searchDone = search.active && search.status === "done";
  const shownRows = search.active ? (searchDone ? (search.data || []) : []) : rows;
  const tapeGroups = useMemo(() => groupFires(rows), [rows]);
  const searchGroups = useMemo(() => groupFires(search.data || []), [search.data]);
  const groups = search.active ? (searchDone ? searchGroups : []) : tapeGroups;
  const sq = search.q.toUpperCase();

  const toggle = useCallback((sym) => {
    setExpanded((cur) => (cur === sym ? null : sym));
  }, []);

  return (
    <div style={s.panel}>
      <div style={s.panelH}>
        <span style={s.panelT}>TICKER CONFLUENCE</span>
        <DataStatus status={status} source="Cross-signal convergence history" />
      </div>
      <div style={s.panelB}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 8, marginBottom: 8,
        }}>
          <span style={{ fontSize: 11, color: C.textDim, fontFamily: font }}>
            <span style={{ color: C.text }}>{shownRows.length}</span> signal{shownRows.length === 1 ? "" : "s"}
            {groups.length > 0 && (
              <> · <span style={{ color: C.text }}>{groups.length}</span> ticker{groups.length === 1 ? "" : "s"}</>
            )}
          </span>
          {/* No refresh button — the 30s poll is the only trigger. */}
          <span style={{ fontSize: 9, color: C.textDim }}>
            {updatedAt ? `Updated ${etClock(new Date(updatedAt).toISOString())} ET` : "—"}
          </span>
        </div>

        <SearchBox value={query} onChange={setQuery} placeholder="Enter a full ticker" />
        {search.active && search.status !== "done" && (
          <div style={{ fontSize: 10, color: C.textDim, marginBottom: 8, lineHeight: 1.6 }}>
            {search.status === "hint" ? "A ticker uses letters, digits, “.” and “-” (up to 10 characters)."
              : search.status === "pending" ? "Enter the full ticker — the search runs when you stop typing."
              : search.status === "loading" ? "Searching…"
              : "Search unavailable. Try again in a moment."}
          </div>
        )}
        {searchDone && shownRows.length > 0 && (
          <div style={{ fontSize: 10, color: C.textDim, marginBottom: 8 }}>
            Showing every stored signal for {sq} (newest {shownRows.length}).
          </div>
        )}

        {search.active && !searchDone ? null
        : searchDone && groups.length === 0 ? (
          <div style={{
            fontSize: 11, color: C.textDim, textAlign: "center",
            padding: 18, lineHeight: 1.7,
          }}>
            No confluence signals for {sq}.<br />
            Search matches the full ticker exactly (for example NVDA, not NVD).
          </div>
        ) : groups.length === 0 ? (
          <div style={{
            fontSize: 11, color: C.textDim, textAlign: "center",
            padding: 18, lineHeight: 1.7,
          }}>
            No confluence signals detected.<br />
            Monitoring dark pool, unusual options, and congressional activity.
          </div>
        ) : (
          <div>
            {groups.map((g) => (
              <ConfluenceRow
                key={g.symbol}
                group={g}
                open={expanded === g.symbol}
                onToggle={toggle}
                onCongressOpen={onCongressOpen}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TickerConfluenceTab;
